import { SignupBotSignals } from 'src/app/core/util/signup-form/signup-form';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { LoggedInResponseInterface } from '../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthBackendService {
  private readonly BASE_ADDRESS = '/user/auth';

  /**
   * @description constructor of AuthBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description logs in user
   * @param {string} username
   * @param {string} password
   * @returns {Promise<any>}
   */
  public login(username: string, password: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.BASE_ADDRESS}/login`, { username, password })
    );
  }

  /**
   * @description logs out user
   * @returns {Promise<any>}
   */
  public logout(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.BASE_ADDRESS}/logout`,
        {},
        { responseType: 'text' as 'json' }
      )
    );
  }

  /**
   * @description checks logged in
   * @returns {Promise<LoggedInResponseInterface>}
   */
  public async checkLoggedIn(): Promise<LoggedInResponseInterface> {
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.BASE_ADDRESS}/is-logged-in`)
      );
      return res;
    } catch (e) {
      return { isLoggedIn: false, isGuest: false };
    }
  }

  /**
   * @description creates and logs in a guest user
   * @returns {Promise<any>}
   */
  public guestLogin(): Promise<any> {
    return firstValueFrom(this.http.post<any>('/guest/login', {}));
  }

  /**
   * @description converts a guest user to a regular user
   * @param {string} email
   * @param {string} password
   * @param {string} username - optional username for the user
   * @returns {Promise<any>}
   */
  public convertGuestToRegular(
    email: string,
    password: string,
    username?: string,
    botSignals?: SignupBotSignals
  ): Promise<any> {
    const body: any = { email, password, ...botSignals };
    if (username) {
      body.username = username;
    }
    return firstValueFrom(
      this.http.post<any>('/guest/convert-to-regular', body)
    );
  }

  /**
   * @description completes Google OAuth registration by providing username and accepting TOS/privacy
   * @param {string} username - the username to use for the account
   * @param {boolean} acceptedTerms - whether the user accepted the terms of service
   * @param {boolean} acceptedPrivacy - whether the user accepted the privacy policy
   * @returns {Promise<any>}
   */
  public completeGoogleRegistration(username: string, acceptedTerms: boolean, acceptedPrivacy: boolean): Promise<any> {
    return firstValueFrom(
      this.http.post<any>('/auth/google/complete-registration', {
        username,
        acceptedTerms,
        acceptedPrivacy
      })
    );
  }

  /**
   * @description disconnects the Google login from the current account
   * @returns {Promise<any>}
   */
  public unlinkGoogle(): Promise<any> {
    return firstValueFrom(this.http.post<any>('/auth/google/unlink', {}));
  }
}
